/**
 * ModeSelector — pantalla de entrada post-login para que el alumno elija
 * qué ejercicio quiere hacer.
 *
 * Corrige un problema estructural del simulador: el modo "grafo 3D con cámara
 * AR" se presentaba como default, haciendo parecer la app académica en
 * lugar de operativa. El feedback clínico del doc fue explícito sobre esto.
 *
 * Ahora el alumno arranca aquí y elige:
 *   🚑 Triage de sala         → Mass Triage (lo más operativo)
 *   🩺 Caso individual          → Monitor hospitalario
 *   🧠 Explorar caso (estudio)  → Grafo 3D (modo avanzado, no default)
 *
 * La última elección se persiste en localStorage. Si el alumno ya lo usó,
 * puede saltear este selector desde el sidebar con "Cambiar modo".
 */

const OVERLAY_ID = 'cgt-mode-selector';
const STYLE_ID = 'cgt-mode-selector-styles';
const LAST_MODE_KEY = 'cgt_last_mode';

export type CgtMode = 'triage-sala' | 'monitor' | 'grafo';

interface ModeOption {
  id: CgtMode;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  badge?: string;
  accent: string;
}

const OPTIONS: ModeOption[] = [
  {
    id: 'triage-sala',
    icon: '🚑',
    title: 'Triage de sala',
    subtitle: 'Ejercicio operativo',
    description: 'Clasificás 6 pacientes llegando en paralelo. Priorizás rápido, la IA te da feedback clínico al final. Formato tipo triage START real.',
    badge: 'Recomendado',
    accent: '#FF453A',
  },
  {
    id: 'monitor',
    icon: '🩺',
    title: 'Caso individual con monitor',
    subtitle: 'UI hospitalaria',
    description: 'Atendés un paciente con monitor multiparamétrico (ECG, SpO₂, NIBP). Tomás acciones clínicas y la IA responde con el efecto realista.',
    accent: '#30D158',
  },
  {
    id: 'grafo',
    icon: '🧠',
    title: 'Explorar caso (estudio)',
    subtitle: 'Modo avanzado',
    description: 'Visualización 3D del caso con física, relaciones y anatomía. Útil para estudio profundo y razonamiento diagnóstico.',
    accent: '#BF5AF2',
  },
];

export class ModeSelector {
  private root: HTMLElement | null = null;

  /**
   * Muestra el selector. Si ya hay un `cgt_last_mode` guardado y `force=false`,
   * no se muestra y se resuelve con esa elección directa.
   */
  show(onSelected: (mode: CgtMode) => void, force = false): void {
    if (this.root) return;
    if (!force) {
      const saved = this.lastMode();
      if (saved) {
        onSelected(saved);
        return;
      }
    }
    this.injectStyles();

    // Oculta el grafo 3D y cualquier UI de modo anterior mientras el selector
    // está visible. Evita SPOILER: al reabrir el selector desde sidebar, el
    // grafo quedaba visible mostrando el diagnóstico resuelto.
    document.body.classList.add('cgt-awaiting-mode');
    document.body.classList.remove('cgt-mode-monitor', 'cgt-mode-triage', 'cgt-mode-grafo');

    const root = document.createElement('div');
    root.id = OVERLAY_ID;
    root.innerHTML = `
      <div class="cgt-ms-backdrop"></div>
      <div class="cgt-ms-card">
        <header>
          <span class="cgt-ms-eyebrow">Dr Newen IA · Clinical triage system</span>
          <h1>¿Qué querés entrenar hoy?</h1>
          <p>Elegí el modo de ejercicio. Podés cambiar después desde el menú lateral.</p>
        </header>
        <div class="cgt-ms-grid">
          ${OPTIONS.map(o => `
            <button type="button" class="cgt-ms-option" data-mode="${o.id}" style="--accent:${o.accent}">
              ${o.badge ? `<span class="cgt-ms-badge">${o.badge}</span>` : ''}
              <div class="cgt-ms-icon">${o.icon}</div>
              <h2>${esc(o.title)}</h2>
              <p class="cgt-ms-sub">${esc(o.subtitle)}</p>
              <p class="cgt-ms-desc">${esc(o.description)}</p>
              <span class="cgt-ms-cta">Comenzar →</span>
            </button>
          `).join('')}
        </div>
        <footer>
          <small>Tip: en Monitor y Triage podés activar la cámara para usar gestos clínicos con las manos.</small>
        </footer>
      </div>
    `;

    root.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.dataset.mode as CgtMode;
        this.setLastMode(mode);
        this.close();
        onSelected(mode);
      });
    });

    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-ms-visible'));
    this.root = root;
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-ms-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  lastMode(): CgtMode | null {
    const v = localStorage.getItem(LAST_MODE_KEY);
    if (v === 'triage-sala' || v === 'monitor' || v === 'grafo') return v;
    return null;
  }

  setLastMode(mode: CgtMode): void {
    localStorage.setItem(LAST_MODE_KEY, mode);
  }

  clearLastMode(): void {
    localStorage.removeItem(LAST_MODE_KEY);
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${OVERLAY_ID} {
        position: fixed; inset: 0; z-index: 16000;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; transition: opacity 0.3s;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      #${OVERLAY_ID}.cgt-ms-visible { opacity: 1; }
      #${OVERLAY_ID} .cgt-ms-backdrop {
        position: absolute; inset: 0;
        background: radial-gradient(circle at 50% 30%, #0f172a 0%, #000 80%);
      }
      #${OVERLAY_ID} .cgt-ms-card {
        position: relative; width: min(1020px, 95vw);
        color: #f5f5f7; padding: 32px;
      }
      #${OVERLAY_ID} header {
        text-align: center; margin-bottom: 28px;
      }
      #${OVERLAY_ID} .cgt-ms-eyebrow {
        display: inline-block; font-size: 10.5px; font-weight: 700;
        letter-spacing: 2px; color: #2997FF; text-transform: uppercase;
        margin-bottom: 12px;
      }
      #${OVERLAY_ID} h1 {
        font-size: 30px; font-weight: 800; letter-spacing: -0.8px;
        margin-bottom: 8px; color: #f5f5f7;
      }
      #${OVERLAY_ID} header p { font-size: 14px; color: #a1a1a6; }
      #${OVERLAY_ID} .cgt-ms-grid {
        display: grid; gap: 16px;
        grid-template-columns: repeat(3, 1fr);
      }
      @media (max-width: 900px) {
        #${OVERLAY_ID} .cgt-ms-grid { grid-template-columns: 1fr; }
      }
      #${OVERLAY_ID} .cgt-ms-option {
        position: relative;
        background: linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%);
        border: 0.5px solid rgba(255,255,255,0.08);
        border-top: 2px solid var(--accent);
        border-radius: 16px; padding: 26px 24px;
        color: inherit; font-family: inherit; cursor: pointer;
        text-align: left;
        display: flex; flex-direction: column; gap: 8px;
        transition: transform 0.15s, border-color 0.15s, background 0.15s;
      }
      #${OVERLAY_ID} .cgt-ms-option:hover {
        transform: translateY(-2px);
        border-color: var(--accent);
        background: linear-gradient(160deg, color-mix(in srgb, var(--accent) 12%, transparent) 0%, rgba(255,255,255,0.02) 100%);
      }
      #${OVERLAY_ID} .cgt-ms-badge {
        position: absolute; top: 14px; right: 14px;
        background: var(--accent); color: #fff;
        font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
        padding: 3px 10px; border-radius: 10px;
        text-transform: uppercase;
      }
      #${OVERLAY_ID} .cgt-ms-icon {
        font-size: 40px; margin-bottom: 6px;
      }
      #${OVERLAY_ID} .cgt-ms-option h2 {
        font-size: 18px; font-weight: 700; letter-spacing: -0.2px;
        color: #f5f5f7;
      }
      #${OVERLAY_ID} .cgt-ms-sub {
        font-size: 10.5px; letter-spacing: 1.2px; text-transform: uppercase;
        color: var(--accent); font-weight: 700;
      }
      #${OVERLAY_ID} .cgt-ms-desc {
        font-size: 13px; color: #a1a1a6; line-height: 1.5;
        flex: 1;
      }
      #${OVERLAY_ID} .cgt-ms-cta {
        font-size: 13px; font-weight: 700; color: var(--accent);
        margin-top: 6px;
      }
      #${OVERLAY_ID} footer {
        margin-top: 22px; text-align: center;
      }
      #${OVERLAY_ID} footer small {
        font-size: 11px; color: #6e6e73; letter-spacing: 0.2px;
      }
    `;
    document.head.appendChild(style);
  }
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}

export const modeSelector = new ModeSelector();
